import { cloneElement, isValidElement, type CSSProperties, type ReactNode } from 'react'
import { Button } from '../Common'

type EmptyStateAction = {
  label: string
  onClick: () => void
  testId?: string
}

type Props = {
  icon?: ReactNode
  title: ReactNode
  body?: ReactNode
  action?: EmptyStateAction
  hint?: ReactNode
  className?: string
}

const SizedIcon = ({ icon }: { icon: ReactNode }) => {
  if (!isValidElement<{ style?: CSSProperties }>(icon)) {
    return <span style={{ fontSize: 32 }}>{icon}</span>
  }

  return cloneElement(icon, {
    style: { fontSize: 32, ...icon.props.style },
  })
}

export const HelperEmptyState = ({ icon, title, body, action, hint, className = '' }: Props) => (
  <div
    className={`flex-1 flex flex-col items-center justify-center gap-3 text-center px-6 ${className}`}
    data-testid="helper-empty-state"
  >
    {icon ? (
      <div className="h-20 w-20 rounded-full bg-[var(--surface-card)] border border-[var(--border-subtle)] grid place-items-center text-[var(--muted)]">
        <SizedIcon icon={icon} />
      </div>
    ) : null}
    <h3 className="text-lg font-semibold text-[var(--text-strong)] max-w-xs">{title}</h3>
    {body ? <p className="text-sm text-[var(--muted)] max-w-xs leading-relaxed">{body}</p> : null}
    {action ? (
      <Button
        variant="tertiary"
        size="md"
        className="mt-2"
        onClick={action.onClick}
        data-testid={action.testId ?? 'helper-empty-state-action'}
      >
        {action.label}
      </Button>
    ) : null}
    {hint ? <p className="text-xs text-[var(--muted)] mt-2">{hint}</p> : null}
  </div>
)

export const HelperEmptyStateSlim = ({ title, body, action, hint, className = '' }: Props) => (
  <div
    className={`flex flex-col items-center gap-2 text-center px-4 py-6 ${className}`}
    data-testid="helper-empty-state-slim"
  >
    <h3 className="text-sm font-semibold text-[var(--text-strong)]">{title}</h3>
    {body ? <p className="text-xs text-[var(--muted)] max-w-xs leading-relaxed">{body}</p> : null}
    {action ? (
      <Button
        variant="tertiary"
        size="md"
        className="mt-1"
        onClick={action.onClick}
        data-testid={action.testId ?? 'helper-empty-state-action'}
      >
        {action.label}
      </Button>
    ) : null}
    {hint ? <p className="text-[11px] text-[var(--muted)] mt-1">{hint}</p> : null}
  </div>
)
